
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Instagram, ShoppingBag, CreditCard, ShoppingCart } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import TranslatedText from '@/TranslatedText';

const Footer: React.FC = () => {
  const isMobile = useIsMobile();
  
  return (
    <footer className={`bg-ahavex-blue text-white w-full ${!isMobile ? 'pl-64' : ''}`}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Get to Know Us */}
          <div>
            <h3 className="text-xl font-semibold mb-6">
              <TranslatedText text="Get to Know Us" />
            </h3>
            <ul className="space-y-3">
              <li>
                <a href="https://group.ahavex.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Ahavex Group" />
                </a>
              </li>
              <li>
                <Link to="/careers" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Careers" />
                </Link>
              </li>
              <li>
                <Link to="/impact" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Impact" />
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Our Business */}
          <div>
            <h3 className="text-xl font-semibold mb-6">
              <TranslatedText text="Our Business" />
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/consultancy" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Consultancy" />
                </Link>
              </li>
              <li>
                <Link to="/web-development" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Web Development" />
                </Link>
              </li>
              <li>
                <Link to="/event-management" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Event Management" />
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Affiliated */}
          <div>
            <h3 className="text-xl font-semibold mb-6">
              <TranslatedText text="Affiliated" />
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/bright-nations" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Bright Nations Initiative" />
                </Link>
              </li>
              <li>
                <Link to="/agencies" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="Agencies" />
                </Link>
              </li>
              <li>
                <Link to="/aida" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="AIDA" />
                </Link>
              </li>
              <li>
                <a href="https://learn.ahavex.com" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200">
                  <TranslatedText text="E-Learning" />
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-6">
            <TranslatedText text="Partners" />
          </h3>
          <div className="flex flex-wrap justify-center gap-6 my-6">
            {/* Popular e-commerce brands */}
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* Nike */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="text-black w-10 h-10">
                <path d="M21.9 8.89l-1.05-4.37c-.22-.9-1-1.52-1.91-1.52H5.05c-.9 0-1.69.63-1.9 1.52L2.1 8.89c-.24 1.02-.02 2.06.62 2.88.08.11.19.19.28.29V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6.94c.09-.09.2-.18.28-.28.64-.82.87-1.87.62-2.89zm-2.99-3.9l1.05 4.37c.1.42.01.84-.25 1.17-.14.18-.44.47-.94.47-.61 0-1.14-.49-1.21-1.14L16.98 5l1.93-.01zM13 5h1.96l.54 4.52c.05.39-.07.78-.33 1.07-.22.26-.54.41-.95.41-.67 0-1.22-.59-1.22-1.31V5zM8.49 9.52L9.04 5H11v4.69c0 .72-.55 1.31-1.29 1.31-.34 0-.65-.15-.89-.41a1.42 1.42 0 0 1-.33-1.07zm-4.45-.16L5.05 5h1.97l-.58 4.86c-.08.65-.6 1.14-1.21 1.14-.49 0-.8-.29-.93-.47-.27-.32-.36-.75-.26-1.17zM5 19v-6.03c.08-.06.16-.03.25-.1.41-.21.75-.55.99-.95.25-.4.35-.86.28-1.31l-.97-8.08h12.89l-.97 8.08c-.07.45.03.91.28 1.31.23.4.58.74.99.95.08.07.17.04.25.1V19H5z"></path>
              </svg>
            </div>
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* Zara */}
              <span className="font-bold text-black text-xl">ZARA</span>
            </div>
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* H&M */}
              <span className="font-bold text-red-600 text-xl">H&M</span>
            </div>
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* Adidas */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="text-black w-10 h-10">
                <path d="M2.5 15.5l8.25-5.5m1 16L24 8.5m-9.75 6.5l8.75-6M2.5 8.5l19.5 13.5"></path>
              </svg>
            </div>
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* Shopping Cart Icon for generic e-commerce */}
              <ShoppingCart className="text-blue-800 w-10 h-10" />
            </div>
            <div className="bg-white rounded-full p-2 w-16 h-16 flex items-center justify-center">
              {/* Shopping Bag Icon for generic e-commerce */}
              <ShoppingBag className="text-green-600 w-10 h-10" />
            </div>
          </div>
        </div>
        
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-6">
            <TranslatedText text="Follow Us" />
          </h3>
          <div className="flex justify-center space-x-6">
            <a 
              href="#" 
              className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200"
              aria-label="Visit our Facebook page"
            >
              <Facebook size={24} />
            </a>
            <a 
              href="#" 
              className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200"
              aria-label="Visit our Twitter page"
            >
              <Twitter size={24} />
            </a>
            <a 
              href="#" 
              className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200"
              aria-label="Visit our LinkedIn page"
            >
              <Linkedin size={24} />
            </a>
            <a 
              href="#" 
              className="text-gray-300 hover:text-ahavex-gold transition-colors duration-200"
              aria-label="Visit our Instagram page"
            >
              <Instagram size={24} />
            </a>
          </div>
        </div>
        
        <div className="mt-12 text-center text-gray-400">
          <p>
            <TranslatedText text="© 2025 Ahavex Group. All rights reserved." />
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
